import Box from "@mui/material/Box";
import {ColumnDef} from "@tanstack/react-table";
import type {AggregationResourceCategoryListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {CommonAggregationService} from "crqlar-react-common/dist/services/aggregation/commonAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const ResourceCategoriesList = () => {
    useTrackScreenVisit('system resource categories');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<AggregationResourceCategoryListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: category => category.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                grow: true
            }
        },
        {
            id: 'sectors',
            header: t('Sectors'),
            accessorFn: category => category.sectors.map(sector => sector.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.sectors.map(sector => sector.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 320,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'sectorName'
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: category => category.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: category => category.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true
            }
        }
    ], [t]);


    const controller = useServerTable({
        tableId: 'system-resource-categories',
        columns,
        getRowId: category => category.id,
        onRowClick: category => navigate(`/system/resourceCategories/${category.id}`),
        initialSorting: [{id: 'sectors', desc: false}],
        queryFn: ({pagination, searchQuery, sorting}) => CommonAggregationService.listResourceCategories({
            name: searchQuery ? [searchQuery] : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        }),
    });

    return (
        <SettingsListPage title={t('Resource categories')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create resource category')}
                            onClick={() => navigate('/system/resourceCategories/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ResourceCategoriesList;
